import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FormDataService } from 'src/app/_services/form-data.service';
import { GlobalService } from 'src/app/_services/global.service';
import { PK_PROVINCE_OPTIONS, countryOfBusiness } from 'src/app/_data/country-states';
import { BusinessSetupService } from 'src/app/_services/business-setup.service';
import { ConnMessageService } from 'src/app/_services/conn-message.service';
import { AppBaseDataService } from 'src/app/_services/app-base-data.service';

@Component({
  selector: 'app-basic-shipping',
  templateUrl: './basic-shipping.component.html',
  styleUrls: ['./basic-shipping.component.scss']
})
export class BasicShippingComponent implements OnInit, OnDestroy {
  /** Shipping costs are in PKR for a Pakistani business unless the caller says otherwise. */
  @Input() currency: string = countryOfBusiness(GlobalService.userInfo?.businessSetup?.[0], GlobalService.countryCode) === 'PK' ? 'PKR' : 'USD';
  @Output() formSubmitted = new EventEmitter<any>();
  @Output() formChanged = new EventEmitter<any>();

  shippingForm: FormGroup;
  rateTypes: any[] = [];
  usStates: any[] = [];

  /** A Pakistani business can ship within Pakistan (all provinces, PKR) as well as to the USA. Others: USA only. */
  readonly isPk = countryOfBusiness(GlobalService.userInfo?.businessSetup?.[0], GlobalService.countryCode) === 'PK';
  readonly countryOptions: { label: string; value: string }[] = this.isPk
    ? [{ label: 'Pakistan', value: 'PK' }, { label: 'USA', value: 'USA' }]
    : [{ label: 'USA', value: 'USA' }];
  readonly pkProvinces: any[] = PK_PROVINCE_OPTIONS.map((p) => ({ name: p.name, code: p.code }));

  /** The states (USA) or provinces (Pakistan) offered for a zone, by its (single) country. */
  regionOptions(zone: any): any[] {
    const countries = zone?.get('countries')?.value;
    return Array.isArray(countries) && countries[0] === 'PK' ? this.pkProvinces : this.usStates;
  }

  regionWord(zone: any): string {
    return this.regionOptions(zone) === this.pkProvinces ? 'provinces' : 'states';
  }

  /** Choosing another country starts the zone over with every state / province of that country selected. */
  onZoneCountryChange(zone: any): void {
    zone.get('states')?.setValue(this.regionOptions(zone).map((s: any) => s.code || s.id));
  }
  
  // USPS Services List - Same as ShippingConfigurationComponent
  servicesList: any[] = [
    { viewValue: 'Express Mail', value: 'express_mail' },
    { viewValue: 'Priority Mail', value: 'priority_mail' },
    { viewValue: 'First Class', value: 'first_class' },
    { viewValue: 'Ground Advantage', value: 'ground_advantage' },
    { viewValue: 'Standard Post', value: 'standard_post' },
    { viewValue: 'Media Mail', value: 'media_mail' },
    { viewValue: 'Library Mail', value: 'library_mail' }
  ];
  
  // UI State
  selectedZoneIndex: number = 0;
  showCityWait: boolean = false;
  zonesList: any[] = [];
  collapsedZones: boolean[] = []; // Track which zones are collapsed
  originalZonesCount: number = 0; // Track original zone count from API
  private isInitialized: boolean = false;
  private deletedZoneIds = new Set<string>();
  submitAttempted: boolean = false;
  
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder, 
    private formDataService: FormDataService,
    private businessSetupService: BusinessSetupService,
    private connMessageService: ConnMessageService,
    private appBaseDataService: AppBaseDataService,
    private confirmationService: ConfirmationService,
    private cdr: ChangeDetectorRef
  ) {
    // Initialize form with all required controls upfront
    this.shippingForm = this.fb.group({
      shippingEnabled: [false],
      zones: this.fb.array([])
    });
  }

  ngOnInit(): void {
    // Component initializes only when triggered, not on page load
    // Do minimal setup here
  }

  /**
   * Initialize the component when it becomes visible/active
   * Call this method when the component is shown (e.g., sidebar opens, accordion expands)
   */
  initializeComponent(): void {
    if (this.isInitialized) {
      return; // Already initialized
    }

    this.isInitialized = true;

    // Load data from services
    this.loadRateTypes();
    this.loadUsStates();

    
      this.loadShippingConfiguration();
 

    // Subscribe to form changes
    this.shippingForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.formChanged.emit(value);
      });
  }

  /**
   * Ensure all required form controls exist in the passed form
   */
  private ensureFormControls(): void {
    const requiredControls = [
      'shippingEnabled',
      'originAddress',
      'originCity',
      'originState',
      'originZip',
      'originCountry',
      'zones'
    ];

    requiredControls.forEach(controlName => {
      if (!this.shippingForm.get(controlName)) {
        if (controlName === 'zones') {
          this.shippingForm.addControl(controlName, this.fb.array([]));
        } else {
          this.shippingForm.addControl(controlName, this.fb.control(''));
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadRateTypes(): void {
    this.formDataService.getRateTypes()
      .pipe(takeUntil(this.destroy$))
      .subscribe(types => {
        this.rateTypes = types;
      });
  }

  private loadUsStates(): void {
    this.formDataService.getUsStates()
      .pipe(takeUntil(this.destroy$))
      .subscribe(states => {
        this.usStates = states;
        // Create default zone if zones array is empty and states are loaded
        if (this.usStates.length > 0) {
          // Small delay to ensure form is ready
          setTimeout(() => {
            if (this.zonesArray.length === 0) {
              this.addDefaultZone();
              this.cdr.detectChanges();
            }
          }, 100);
        }
      });
  }

  private createDefaultZone(): FormGroup {
    // Every state (USA) or province (Pakistan) code
    const allUsStateCodes = (this.isPk ? this.pkProvinces : this.usStates).map(s => s.code || s.id);

    return this.fb.group({
      id: [null], // Zone ID from API
      isEnabled: [true],
      zoneName: ['Default Zone'],
      countries: [[this.isPk ? 'PK' : 'USA']],
      states: [allUsStateCodes],
      rateType: ['flat', Validators.required],
      flatRate: [0, [Validators.required, Validators.min(0)]],
      isFlatTaxIncluded: [false],
      flatTaxRate: [0, [Validators.required, Validators.min(0)]],
      isEnablePickup: [false],
      pickupCost: [0, [Validators.min(0)]],
      isPickUpTaxIncluded: [false],
      pickUpTaxRate: [0, [Validators.required, Validators.min(0)]],
      uspsEnabled: [false],
      uspsServices: [[]],
      flatRateId: [null], // Nested IDs from API
      storePickUpId: [null],
      uspsId: [null],
      uspsUserID: [null]
    });
  }

  private addDefaultZone(): void {
    if (this.usStates.length > 0 && this.zonesArray.length === 0) {
      this.zonesArray.push(this.createDefaultZone());
      this.collapsedZones = [false]; // Default zone is open
      this.onZoneRateTypeChange(0);
    }
  }

  get zonesArray(): FormArray {
    return this.shippingForm.get('zones') as FormArray;
  }

  addZone(): void {
    this.clearStaleDeletedZonesFromMemory();

    // Close all existing zones
    this.collapsedZones = this.collapsedZones.map(() => true);
    
    const newZone = this.fb.group({
      id: [null], // Zone ID (null for new zones)
      isEnabled: [true],
      zoneName: ['New Zone'],
      countries: [[this.isPk ? 'PK' : 'USA']],
      states: [[]],
      rateType: ['flat', Validators.required],
      flatRate: [0, [Validators.required, Validators.min(0)]],
      isFlatTaxIncluded: [false],
      flatTaxRate: [0, [Validators.required, Validators.min(0)]],
      isEnablePickup: [false],
      pickupCost: [0, [Validators.min(0)]],
      isPickUpTaxIncluded: [false],
      pickUpTaxRate: [0, [Validators.required, Validators.min(0)]],
      uspsEnabled: [false],
      uspsServices: [[]],
      flatRateId: [null], // Nested IDs (null for new zones)
      storePickUpId: [null],
      uspsId: [null],
      uspsUserID: [null]
    });
    this.zonesArray.push(newZone);
    
    // Keep the newly added zone open
    this.collapsedZones.push(false);
    this.onZoneRateTypeChange(this.zonesArray.length - 1);
    
    this.cdr.detectChanges();
  }

  removeZone(index: number): void {
    this.confirmationService.confirm({
      header: 'Delete Zone',
      message: 'Are you sure you want to delete this zone?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => this.deleteZone(index)
    });
  }

  private deleteZone(index: number): void {
    const zone = this.zonesArray.at(index) as FormGroup;
    if (!zone) return;

    const rawZoneId = zone.get('id')?.value;
    const zoneId =
      rawZoneId !== null && rawZoneId !== undefined && String(rawZoneId).trim() !== ''
        ? String(rawZoneId).trim()
        : null;

    // Unsaved zone: remove locally only.
    if (!zoneId) {
      this.removeZoneFromForm(index);
      return;
    }

    this.appBaseDataService.showSpinner();
    this.businessSetupService
      .deleteShippingConfiguration(zoneId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res: any) => {
          this.appBaseDataService.hideSpinner();
          if (res?.success === false) {
            this.connMessageService.showToastMessage('Error deleting zone', 'error');
            return;
          }

          this.deletedZoneIds.add(zoneId);
          this.removeZoneFromForm(index);
          this.loadShippingConfiguration();
          this.connMessageService.showToastMessage('Zone deleted successfully!', 'success');
        },
        (error: any) => {
          this.appBaseDataService.hideSpinner();
          console.error('Error deleting zone:', error);
          this.connMessageService.showToastMessage('Error deleting zone', 'error');
        }
      );
  }

  private removeZoneFromForm(index: number): void {
    if (index < 0 || index >= this.zonesArray.length) return;

    this.zonesArray.removeAt(index);
    this.collapsedZones.splice(index, 1);

    if (this.zonesArray.length === 0) {
      this.shippingForm.patchValue({ shippingEnabled: false }, { emitEvent: false });
    }

    if (this.originalZonesCount > 0) {
      this.originalZonesCount -= 1;
    }

    if (this.selectedZoneIndex >= this.zonesArray.length) {
      this.selectedZoneIndex = this.zonesArray.length - 1;
    }
    if (this.selectedZoneIndex < 0) {
      this.selectedZoneIndex = 0;
    }

    if (this.collapsedZones.length > 0 && !this.collapsedZones.some((isCollapsed) => !isCollapsed)) {
      const targetOpenIndex = Math.min(index, this.collapsedZones.length - 1);
      this.selectedZoneIndex = targetOpenIndex;
      this.collapsedZones[targetOpenIndex] = false;
    }

    this.formChanged.emit(this.shippingForm.value);
    this.cdr.detectChanges();
  }

  onZonePanelToggle(index: number, event: any): void {
    if (index < 0 || index >= this.collapsedZones.length) return;
    const isCollapsed = !!event?.collapsed;
    this.collapsedZones[index] = isCollapsed;
    if (!isCollapsed) {
      this.selectedZoneIndex = index;
    }
  }

  private getOpenZonePreference(): { zoneId: string | null; zoneName: string | null; index: number } | null {
    if (!this.zonesArray?.length) return null;

    let openIndex = this.collapsedZones.findIndex((isCollapsed) => !isCollapsed);
    if (openIndex < 0) {
      openIndex = this.selectedZoneIndex;
    }
    if (openIndex < 0 || openIndex >= this.zonesArray.length) return null;

    const zone = this.zonesArray.at(openIndex) as FormGroup;
    const rawId = zone?.get('id')?.value;
    const zoneId =
      rawId !== null && rawId !== undefined && String(rawId).trim() !== ''
        ? String(rawId).trim()
        : null;
    const zoneName = String(zone?.get('zoneName')?.value ?? '').trim() || null;

    return { zoneId, zoneName, index: openIndex };
  }

  private resolveOpenZoneIndex(
    zones: any[],
    preference: { zoneId: string | null; zoneName: string | null; index: number } | null
  ): number {
    if (!Array.isArray(zones) || zones.length < 1) return -1;
    if (!preference) return 0;

    if (preference.zoneId) {
      const byId = zones.findIndex((zone) => String(zone?.id ?? '').trim() === preference.zoneId);
      if (byId >= 0) return byId;
    }

    if (preference.zoneName) {
      const targetName = preference.zoneName.toLowerCase();
      const byName = zones.findIndex((zone) => String(zone?.zoneName ?? '').trim().toLowerCase() === targetName);
      if (byName >= 0) return byName;
    }

    if (Number.isInteger(preference.index)) {
      return Math.min(Math.max(preference.index, 0), zones.length - 1);
    }

    return 0;
  }

  onZoneRateTypeChange(index: number): void {
    const zone = this.zonesArray.at(index) as FormGroup;
    if (!zone) return;

    const rateTypeControl = zone.get('rateType');
    const rateType = this.normalizeRateType(rateTypeControl?.value, {
      uspsEnabled: zone.get('uspsEnabled')?.value === true
    });
    if (rateTypeControl && rateTypeControl.value !== rateType) {
      rateTypeControl.setValue(rateType, { emitEvent: false });
    }
    const flatRateControl = zone.get('flatRate');
    const isFlatTaxIncludedControl = zone.get('isFlatTaxIncluded');
    const isEnablePickupControl = zone.get('isEnablePickup');
    const pickupCostControl = zone.get('pickupCost');
    const isPickUpTaxIncludedControl = zone.get('isPickUpTaxIncluded');
    const uspsEnabledControl = zone.get('uspsEnabled');
    const uspsServicesControl = zone.get('uspsServices');

    if (!flatRateControl) return;

    if (isFlatTaxIncludedControl?.value === null || isFlatTaxIncludedControl?.value === undefined) {
      isFlatTaxIncludedControl.setValue(false, { emitEvent: false });
    }
    if (isEnablePickupControl?.value === null || isEnablePickupControl?.value === undefined) {
      isEnablePickupControl.setValue(true, { emitEvent: false });
    }
    if (pickupCostControl?.value === null || pickupCostControl?.value === undefined) {
      pickupCostControl.setValue(0, { emitEvent: false });
    }
    if (isPickUpTaxIncludedControl?.value === null || isPickUpTaxIncludedControl?.value === undefined) {
      isPickUpTaxIncludedControl.setValue(false, { emitEvent: false });
    }

    if (rateType === 'flat') {
      flatRateControl.enable({ emitEvent: false });
      flatRateControl.setValidators([Validators.required, Validators.min(0.01)]);
      uspsEnabledControl?.setValue(false, { emitEvent: false });
      uspsServicesControl?.setValue([], { emitEvent: false });
    } else if (rateType === 'free') {
      // Free shipping should always carry a zero amount.
      flatRateControl.setValue(0, { emitEvent: false });
      flatRateControl.clearValidators();
      flatRateControl.disable({ emitEvent: false });
      uspsEnabledControl?.setValue(false, { emitEvent: false });
      uspsServicesControl?.setValue([], { emitEvent: false });
    } else {
      // Calculated rates do not use a fixed shipping amount.
      flatRateControl.setValue(0, { emitEvent: false });
      flatRateControl.clearValidators();
      flatRateControl.disable({ emitEvent: false });
      if (uspsEnabledControl?.value === null || uspsEnabledControl?.value === undefined) {
        uspsEnabledControl?.setValue(false, { emitEvent: false });
      }
      if (!Array.isArray(uspsServicesControl?.value)) {
        uspsServicesControl?.setValue([], { emitEvent: false });
      }
    }

    flatRateControl.updateValueAndValidity({ emitEvent: false });
    this.onFlatTaxToggle(index);
    this.onPickupToggle(index);
    this.onPickupTaxToggle(index);
  }

  private hasInvalidFlatRateZones(): boolean {
    let hasInvalid = false;
    for (let i = 0; i < this.zonesArray.length; i++) {
      const zone = this.zonesArray.at(i) as FormGroup;
      const rateType = this.normalizeRateType(zone.get('rateType')?.value, {
        uspsEnabled: zone.get('uspsEnabled')?.value === true
      });
      if (rateType !== 'flat') {
        continue;
      }

      this.onZoneRateTypeChange(i);

      const flatRateControl = zone.get('flatRate');
      const flatRate = Number(flatRateControl?.value ?? 0);
      if (!Number.isFinite(flatRate) || flatRate <= 0) {
        flatRateControl?.markAsTouched();
        flatRateControl?.updateValueAndValidity({ emitEvent: false });
        hasInvalid = true;
      }
    }
    return hasInvalid;
  }

  /**
   * Validate duplicate zone names
   */
  doesZoneNameExist(zoneName: string, excludeIndex?: number): boolean {
    if (!zoneName || zoneName.trim() === '') return false;
    
    const trimmedName = zoneName.trim().toLowerCase();
    
    for (let i = 0; i < this.zonesArray.length; i++) {
      // Skip the zone being edited if excludeIndex is provided
      if (excludeIndex !== undefined && i === excludeIndex) {
        continue;
      }
      
      const zone = this.zonesArray.at(i) as FormGroup;
      const existingName = zone.get('zoneName')?.value;
      
      if (existingName && existingName.trim().toLowerCase() === trimmedName) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Validate zone name and set errors if duplicate
   */
  validateZoneName(index: number): void {
    const zone = this.zonesArray.at(index) as FormGroup;
    const zoneName = zone.get('zoneName')?.value;
    
    if (this.doesZoneNameExist(zoneName, index)) {
      zone.get('zoneName')?.setErrors({ 'zoneExists': true });
    } else {
      const control = zone.get('zoneName');
      if (control?.hasError('zoneExists')) {
        control.setErrors(null);
      }
    }
  }

  /**
   * Handle pickup cost toggle
   */
  selectOnFocus(event: Event): void {
    const input = event?.target as HTMLInputElement;
    if (input && typeof input.select === 'function') {
      setTimeout(() => input.select());
    }
  }

  onPickupToggle(index: number): void {
    const zone = this.zonesArray.at(index) as FormGroup;
    const isPickupEnabled = zone.get('isEnablePickup')?.value;
    const pickupCostControl = zone.get('pickupCost');
    const pickUpTaxRateControl = zone.get('pickUpTaxRate');

    if (isPickupEnabled) {
      pickupCostControl?.setValidators([Validators.required, Validators.min(0)]);
      pickUpTaxRateControl?.setValidators([Validators.required, Validators.min(0)]);
    } else {
      pickupCostControl?.clearValidators();
      pickUpTaxRateControl?.clearValidators();
    }

    pickupCostControl?.updateValueAndValidity({ emitEvent: false });
    pickUpTaxRateControl?.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Handle flat tax included toggle
   */
  onFlatTaxToggle(index: number): void {
    const zone = this.zonesArray.at(index) as FormGroup;
    const isFlatRate = zone.get('rateType')?.value === 'flat';
    const isTaxIncluded = zone.get('isFlatTaxIncluded')?.value;
    const flatTaxRateControl = zone.get('flatTaxRate');

    if (!isFlatRate) {
      flatTaxRateControl?.clearValidators();
      flatTaxRateControl?.setValue(0, { emitEvent: false });
    } else if (isTaxIncluded) {
      flatTaxRateControl?.clearValidators();
      flatTaxRateControl?.setValue(0, { emitEvent: false });
    } else {
      flatTaxRateControl?.setValidators([Validators.required, Validators.min(0)]);
    }

    flatTaxRateControl?.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Handle pickup tax included toggle
   */
  onPickupTaxToggle(index: number): void {
    const zone = this.zonesArray.at(index) as FormGroup;
    const isPickupEnabled = zone.get('isEnablePickup')?.value;
    const isTaxIncluded = zone.get('isPickUpTaxIncluded')?.value;
    const pickUpTaxRateControl = zone.get('pickUpTaxRate');

    if (!isPickupEnabled) {
      pickUpTaxRateControl?.clearValidators();
      pickUpTaxRateControl?.setValue(0, { emitEvent: false });
    } else if (isTaxIncluded) {
      pickUpTaxRateControl?.clearValidators();
      pickUpTaxRateControl?.setValue(0, { emitEvent: false });
    } else {
      pickUpTaxRateControl?.setValidators([Validators.required, Validators.min(0)]);
    }

    pickUpTaxRateControl?.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Get header text for zone panel
   */
  getZoneTabHeader(index: number): string {
    const zone = this.zonesArray.at(index) as FormGroup;
    const zoneName = zone.get('zoneName')?.value || `Zone ${index + 1}`;
    return zoneName;
  }

  /**
   * Zipcode lookup to auto-fill city and state
   */
  zipcodeInputEvent(value: string): void {
    if (!value || value.length !== 5) return;
    
    this.showCityWait = true;
    this.appBaseDataService.getCityAndStateInfo(value).subscribe(
      (res: any) => {
        this.showCityWait = false;
        if (res.success && res.data?.city !== undefined) {
          console.log('Zipcode lookup successful:', res.data);
          // Zipcode lookup succeeded - can be used to update origin address if needed
          this.cdr.detectChanges();
        } else {
          console.warn('Invalid zipcode or no data found');
          this.showCityWait = false;
        }
      },
      (error: any) => {
        this.showCityWait = false;
        console.error('Error in zipcode lookup:', error);
      }
    );
  }

  getRateTypeLabel(code: string): string {
    const rateType = this.rateTypes.find(rt => rt.code === code);
    return rateType?.name || code;
  }

  canRemoveZone(): boolean {
    return true; // Can always disable/delete a zone by marking as disabled
  }

  onToggleShipping(): void {
    const isEnabled = this.shippingForm.get('shippingEnabled')?.value;
    this.formChanged.emit(this.shippingForm.value);
  }

  onSubmit(): void {
    // Validate all zone names for duplicates before saving
    let hasDuplicates = false;
    for (let i = 0; i < this.zonesArray.length; i++) {
      this.validateZoneName(i);
      const zone = this.zonesArray.at(i) as FormGroup;
      if (zone.get('zoneName')?.hasError('zoneExists')) {
        hasDuplicates = true;
      }
    }
    
    if (hasDuplicates) {
      this.connMessageService.showToastMessage('A zone with name already exisits, please choose unique zone name', 'warn');
      return;
    }
    this.submitAttempted = true;

    if (this.hasInvalidFlatRateZones()) {
      this.connMessageService.showToastMessage('For Flat Rate, shipping cost must be greater than 0.', 'warn');
      return;
    }
    
    if (this.shippingForm.valid) {
      this.saveShippingConfiguration();
    } else {
      this.shippingForm.markAllAsTouched();
      this.connMessageService.showToastMessage('Please fix validation errors before saving.', 'warn');
    }
  }

  resetForm(): void {
    this.shippingForm.reset({
      shippingEnabled: false,
      zones: []
    }, { emitEvent: false });
    
    // Reset collapsedZones
    this.collapsedZones = [];
    
    // Re-add default zone
    this.addDefaultZone();
    this.formChanged.emit(this.shippingForm.value);
  }

  /**
   * Load shipping configuration from API
   */
  loadShippingConfiguration(): void {
    try {
      const openZonePreference = this.getOpenZonePreference();
      // Don't show spinner on auto-load, only on manual reload
      this.businessSetupService.getShippingConfiguration()
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (res: any) => {
            if (res.success && res.data) {
              const configData = res.data;
              
              // Extract zones - handle multiple response formats
              let zones: any[] = [];
              
              if (Array.isArray(configData)) {
                zones = configData;
              } else if (configData.zones && Array.isArray(configData.zones)) {
                zones = configData.zones;
              }

              zones = this.filterDeletedZones(zones);
              
              if (zones && zones.length > 0) {
                // Store original zone count for later use
                this.originalZonesCount = zones.length;
                console.log('Zones loaded:', zones.length);
                this.populateFormFromAPI(zones, openZonePreference);
                this.cdr.detectChanges();
              } else {
                // If no existing config, create default zone
                this.originalZonesCount = 0;
                this.shippingForm.patchValue({ shippingEnabled: false }, { emitEvent: false });
                if (this.zonesArray.length === 0 && this.usStates.length > 0) {
                  this.addDefaultZone();
                  this.cdr.detectChanges();
                }
              }
            } else {
              // If no existing config, create default zone
              this.originalZonesCount = 0;
              this.shippingForm.patchValue({ shippingEnabled: false }, { emitEvent: false });
              if (this.zonesArray.length === 0 && this.usStates.length > 0) {
                this.addDefaultZone();
                this.cdr.detectChanges();
              }
            }
          },
          (error: any) => {
            console.error('Error loading shipping configuration:', error);
            // If no existing config, create default zone
            this.originalZonesCount = 0;
            this.shippingForm.patchValue({ shippingEnabled: false }, { emitEvent: false });
            if (this.zonesArray.length === 0 && this.usStates.length > 0) {
              this.addDefaultZone();
              this.cdr.detectChanges();
            }
          }
        );
    } catch (error) {
      console.error('Error in loadShippingConfiguration:', error);
      // If error, create default zone
      this.originalZonesCount = 0;
      this.shippingForm.patchValue({ shippingEnabled: false }, { emitEvent: false });
      if (this.zonesArray.length === 0 && this.usStates.length > 0) {
        this.addDefaultZone();
        this.cdr.detectChanges();
      }
    }
  }

  /**
   * Populate form from API response data
   */
  private populateFormFromAPI(
    zones: any[],
    openZonePreference: { zoneId: string | null; zoneName: string | null; index: number } | null = null
  ): void {
    if (!zones || zones.length === 0) return;

    try {
      // Clear existing zones
      while (this.zonesArray.length > 0) {
        this.zonesArray.removeAt(0);
      }
      
      // Reset collapsedZones array
      this.collapsedZones = [];

      // Process each zone from API
      zones.forEach((zone: any) => {
        const zoneFormGroup = this.fb.group({
          id: [zone.id || null], // Store zone ID from API
          isEnabled: [zone.isEnabled !== false],
          zoneName: [zone.zoneName || 'Zone'],
          countries: [zone.countries || ['USA']],
          states: [this.extractStateCodes(zone.states) || []],
          rateType: [this.determineRateType(zone), Validators.required],
          flatRate: [zone.flatRate?.rate || 0, [Validators.required, Validators.min(0)]],
          isFlatTaxIncluded: [false],
          flatTaxRate: [zone.flatRate?.taxRate || 0, [Validators.required, Validators.min(0)]],
          isEnablePickup: [zone.storePickUp?.isEnablePickup !== false],
          pickupCost: [zone.storePickUp?.pickupCost || 0, [Validators.min(0)]],
          isPickUpTaxIncluded: [false],
          pickUpTaxRate: [zone.storePickUp?.taxRate || 0, [Validators.required, Validators.min(0)]],
          uspsEnabled: [zone.usps?.isEnabledUsps || false],
          uspsServices: [zone.usps?.enabledServices || []],
          flatRateId: [zone.flatRate?.id || null], // Store nested IDs
          storePickUpId: [zone.storePickUp?.id || null],
          uspsId: [zone.usps?.id || null],
          uspsUserID: [zone.usps?.userID || null]
        });
        this.zonesArray.push(zoneFormGroup);
        // Keep all zones collapsed initially (true = collapsed)
        this.collapsedZones.push(true);
      });

      for (let i = 0; i < this.zonesArray.length; i++) {
        this.onZoneRateTypeChange(i);
      }

      // Enable shipping when any available zone is enabled.
      const hasEnabledZone = zones.some(zone => zone?.isEnabled !== false);
      this.shippingForm.patchValue({
        shippingEnabled: hasEnabledZone
      }, { emitEvent: false });
      
      const openIndex = this.resolveOpenZoneIndex(zones, openZonePreference);
      if (openIndex >= 0 && this.collapsedZones.length > 0) {
        this.selectedZoneIndex = openIndex;
        this.collapsedZones[openIndex] = false;
      }
      
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error populating form from API:', error);
    }
  }

  /**
   * Extract state codes from API response format
   */
  private extractStateCodes(statesObject: any): string[] {
    if (!statesObject) return [];
    
    const stateCodes: string[] = [];
    Object.values(statesObject).forEach((codes: any) => {
      if (Array.isArray(codes)) {
        stateCodes.push(...codes);
      }
    });
    return stateCodes;
  }

  /**
   * Determine rate type from zone configuration
   */
  private determineRateType(zone: any): string {
    const shippingTypeValue = Number(zone?.shippingtype ?? zone?.shippingType ?? 0);
    if (shippingTypeValue === 1) return 'flat';
    if (shippingTypeValue === 2) return 'free';
    if (shippingTypeValue === 3 || shippingTypeValue === 4) return 'calc';

    return this.normalizeRateType(zone?.shippingtype ?? zone?.shippingType, {
      flatEnabled: zone?.flatRate?.isEnableFlat === true,
      uspsEnabled: zone?.usps?.isEnabledUsps === true
    });
  }

  private normalizeRateType(
    rateType: any,
    options: { flatEnabled?: boolean; uspsEnabled?: boolean } = {}
  ): 'flat' | 'free' | 'calc' {
    const raw = String(rateType?.code ?? rateType?.value ?? rateType ?? '')
      .trim()
      .toLowerCase();

    if (raw === '1' || raw === 'flat' || raw === 'flat rate' || raw === 'flatrate') return 'flat';
    if (raw === '2' || raw === 'free' || raw === 'free shipping' || raw === 'freeshipping') return 'free';
    if (raw === '3' || raw === '4' || raw === 'calc' || raw === 'calculated' || raw === 'calculated rates' || raw === 'usps') return 'calc';

    if (options.flatEnabled === true) return 'flat';
    if (options.uspsEnabled === true) return 'calc';
    return 'free';
  }

  private mapRateTypeToShippingType(rateType: any): number {
    switch (this.normalizeRateType(rateType)) {
      case 'flat':
        return 1;
      case 'free':
        return 2;
      case 'calc':
        return 3;
      default:
        return 1;
    }
  }

  private filterDeletedZones(zones: any[]): any[] {
    if (!Array.isArray(zones) || this.deletedZoneIds.size === 0) {
      return zones || [];
    }

    return zones.filter((zone: any) => {
      const zoneId = zone?.id;
      if (zoneId === null || zoneId === undefined || String(zoneId).trim() === '') {
        return true;
      }
      return !this.deletedZoneIds.has(String(zoneId).trim());
    });
  }

  private clearStaleDeletedZonesFromMemory(): void {
    if (this.deletedZoneIds.size === 0 || this.zonesArray.length === 0) {
      return;
    }

    for (let i = this.zonesArray.length - 1; i >= 0; i--) {
      const zone = this.zonesArray.at(i) as FormGroup;
      const zoneId = zone.get('id')?.value;
      if (zoneId !== null && zoneId !== undefined && String(zoneId).trim() !== '') {
        const normalizedId = String(zoneId).trim();
        if (this.deletedZoneIds.has(normalizedId)) {
          this.zonesArray.removeAt(i);
          this.collapsedZones.splice(i, 1);
        }
      }
    }
  }

  /**
   * Save shipping configuration to API
   */
  saveShippingConfiguration(): void {
    if (!this.shippingForm.valid) {
      this.connMessageService.showToastMessage('Please fill in all required fields', 'warn');
      return;
    }

    try {
      this.appBaseDataService.showSpinner();
      
      const shippingData = this.prepareShippingData();
      
      // If zones exist (more than 0), use UPDATE (PUT), otherwise use SAVE (POST)
      const hasExistingZones = this.originalZonesCount > 0;
      console.log('Original zones count:', this.originalZonesCount);
      console.log('Using method:', hasExistingZones ? 'UPDATE (PUT)' : 'SAVE (POST)');
      
      const apiCall$ = hasExistingZones
        ? this.businessSetupService.updateShippingConfiguration(null, shippingData)
        : this.businessSetupService.saveShippingConfiguration(shippingData);
      
      apiCall$
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (res: any) => {
            this.appBaseDataService.hideSpinner();
            if (res.success) {
              this.connMessageService.showToastMessage('Shipping configuration saved successfully!', 'success');
              this.formSubmitted.emit(this.shippingForm.value);
              // Reload configuration after save
              this.loadShippingConfiguration();
            } else {
              this.connMessageService.showToastMessage('Error saving shipping configuration', 'error');
            }
          },
          (error: any) => {
            this.appBaseDataService.hideSpinner();
            console.error('Error saving shipping configuration:', error);
            this.connMessageService.showToastMessage('Error saving shipping configuration', 'error');
          }
        );
    } catch (error) {
      this.appBaseDataService.hideSpinner();
      console.error('Error in saveShippingConfiguration:', error);
      this.connMessageService.showToastMessage('Error saving shipping configuration', 'error');
    }
  }

  /**
   * Prepare shipping data for API submission
   * Uses business address from GlobalService for every zone's shipping origin
   */
  private prepareShippingData(): any {
    // Get business address from GlobalService
    const businessSetup = GlobalService.userInfo?.businessSetup?.[0];
    const businessAddress = businessSetup?.address?.split(' || ')?.[0]?.trim() || businessSetup?.address || '';
    
    const zones = this.zonesArray.value.map((zone: any) => {
      // Build states object: { "US": [state_codes] }
      const statesObject: any = {};
      if (zone.countries && zone.countries.length > 0) {
        zone.countries.forEach((country: string) => {
          statesObject[country] = zone.states || [];
        });
      }

      const normalizedRateType = this.normalizeRateType(zone.rateType, {
        flatEnabled: zone?.flatRate?.isEnableFlat === true,
        uspsEnabled: zone?.uspsEnabled === true
      });
      const isPickupEnabled = zone.isEnablePickup !== false;
      const isFlatTaxIncluded = zone.isFlatTaxIncluded !== false;
      const isPickUpTaxIncluded = false;
      const isCalculatedRates = normalizedRateType === 'calc';
      const isUspsEnabled = isCalculatedRates && zone.uspsEnabled === true;

      const zoneData: any = {
        isEnabled: zone.isEnabled,
        zoneName: zone.zoneName,
        shippingtype: this.mapRateTypeToShippingType(normalizedRateType),
        country: businessSetup?.country || 'US',
        // Use business address for every zone's shipping origin
        address: businessAddress,
        phone: businessSetup?.phone || '',
        city: businessSetup?.city || '',
        zipCode: businessSetup?.zip || '',
        state: businessSetup?.state || '',
        countries: zone.countries || ['USA'],
        states: statesObject,
        isSaved: true,
        flatRate: {
          isEnableFlat: normalizedRateType === 'flat',
          rate: normalizedRateType === 'flat' ? Number(zone.flatRate) : 0,
          isTaxIncluded: isFlatTaxIncluded,
          taxRate: isFlatTaxIncluded ? 0 : Number(zone.flatTaxRate)
        },
        storePickUp: {
          isEnablePickup: isPickupEnabled,
          pickupCost: isPickupEnabled ? Number(zone.pickupCost) : 0,
          isTaxIncluded: isPickUpTaxIncluded,
          taxRate: isPickUpTaxIncluded ? 0 : Number(zone.pickUpTaxRate)
        },
        usps: {
          isEnabledUsps: isUspsEnabled,
          postcode: businessSetup?.zip || '',
          enabledServices: isCalculatedRates && Array.isArray(zone.uspsServices) ? zone.uspsServices : []
        }
      };

      // Add zone ID if it exists (for updates)
      if (zone.id) {
        zoneData.id = zone.id;
      }

      // Add nested IDs if they exist
      if (zone.flatRateId) {
        zoneData.flatRate.id = zone.flatRateId;
      }
      if (zone.storePickUpId) {
        zoneData.storePickUp.id = zone.storePickUpId;
      }
      if (zone.uspsId) {
        zoneData.usps.id = zone.uspsId;
      }
      if (zone.uspsUserID) {
        zoneData.usps.userID = zone.uspsUserID;
      }

      return zoneData;
    });

    return {
      zones: zones
    };
  }

  /**
   * Update existing shipping configuration
   */
  updateShippingConfiguration(zoneId: string): void {
    if (!this.shippingForm.valid) {
      this.connMessageService.showToastMessage('Please fill in all required fields', 'warn');
      return;
    }

    try {
      this.appBaseDataService.showSpinner();
      
      const shippingData = this.prepareShippingData();
      
      this.businessSetupService.updateShippingConfiguration(zoneId, shippingData)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (res: any) => {
            this.appBaseDataService.hideSpinner();
            if (res.success) {
              this.connMessageService.showToastMessage('Shipping configuration updated successfully!', 'success');
              this.formSubmitted.emit(this.shippingForm.value);
              // Reload configuration after update
              this.loadShippingConfiguration();
            } else {
              this.connMessageService.showToastMessage('Error updating shipping configuration', 'error');
            }
          },
          (error: any) => {
            this.appBaseDataService.hideSpinner();
            console.error('Error updating shipping configuration:', error);
            this.connMessageService.showToastMessage('Error updating shipping configuration', 'error');
          }
        );
    } catch (error) {
      this.appBaseDataService.hideSpinner();
      console.error('Error in updateShippingConfiguration:', error);
      this.connMessageService.showToastMessage('Error updating shipping configuration', 'error');
    }
  }

  // Getters for template
  get isShippingEnabled(): boolean {
    return this.shippingForm.get('shippingEnabled')?.value || false;
  }

  /**
   * Get origin address data from business setup
   */
  getOriginData(): any {
    const businessSetup = GlobalService.userInfo?.businessSetup?.[0];
    const businessAddress = businessSetup?.address?.split(' || ')?.[0]?.trim() || businessSetup?.address || '';
    
    return {
      address: businessAddress,
      city: businessSetup?.city || '',
      state: businessSetup?.state || '',
      zip: businessSetup?.zip || '',
      country: businessSetup?.country || 'USA'
    };
  }

  /**
   * Get all shipping form data
   */
  getShippingFormData(): any {
    return this.shippingForm.value;
  }
}
